/* Shared Courier-Setup page hero — matches the main-app home banner
   (dark navy gradient + radial glow + blue icon tile + white title/subtitle).
   Props: icon (MUI icon element), title, subtitle, actions (right-side node),
   eyebrow (optional small node above the title, e.g. a breadcrumb). */
import React from 'react'
import PropTypes from 'prop-types'
import {Box, Typography} from '@mui/material'
import {alpha} from '@mui/material/styles'

/* shared style for outlined action buttons sitting on the dark hero */
export const HERO_BTN = {
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '10px',
  color: '#fff',
  borderColor: 'rgba(255,255,255,.3)',
  flexShrink: 0,
  '&:hover': {borderColor: '#fff', bgcolor: 'rgba(255,255,255,.08)'},
}

/* shared style for the solid/primary action button on the dark hero.
   NOTE: use `background` (not bgcolor) — the supply theme styles contained
   buttons with a gradient `background`, which would otherwise bleed through. */
export const HERO_BTN_SOLID = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: '10px',
  flexShrink: 0,
  background: 'linear-gradient(135deg, #2065D1, #1B4FA8)',
  color: '#fff',
  boxShadow: '0 6px 16px rgba(32,101,209,.35)',
  '&:hover': {background: 'linear-gradient(135deg, #1B57BC, #164088)', boxShadow: '0 6px 16px rgba(32,101,209,.45)'},
}

export default function SupplyHero({icon, eyebrow, title, subtitle, actions, subtle = false, sx}) {
  if (subtle) {
    // editorial light header — no card; type + whitespace carry it, one focal icon,
    // a clean hairline separates it from the page content.
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.75,
          flexWrap: 'wrap',
          p: {xs: '14px 20px', md: '16px 26px'},
          mb: 3,
          borderRadius: '14px',
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          // soft blue wash fading off to the right — gentle presence, not a slab
          backgroundImage: (th) => `linear-gradient(100deg, ${alpha(th.palette.primary.main, 0.06)} 0%, ${alpha(th.palette.primary.main, 0)} 55%)`,
          ...sx,
        }}
      >
        {icon ? (
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              flexShrink: 0,
              color: 'primary.main',
              bgcolor: (th) => alpha(th.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& svg': {fontSize: 23},
            }}
          >
            {icon}
          </Box>
        ) : null}
        <Box sx={{minWidth: 0, flex: 1}}>
          {eyebrow ? <Box sx={{mb: 0.5}}>{eyebrow}</Box> : null}
          <Typography sx={{fontSize: 22, fontWeight: 800, letterSpacing: '-.4px', color: 'text.primary', lineHeight: 1.15}}>{title}</Typography>
          {subtitle ? <Typography sx={{fontSize: 13, color: 'text.secondary', mt: 0.5, lineHeight: 1.55, maxWidth: 760}}>{subtitle}</Typography> : null}
        </Box>
        {actions ? (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, flexWrap: 'wrap'}}>{actions}</Box>
        ) : null}
      </Box>
    )
  }
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        p: {xs: '16px 22px', md: '18px 32px'},
        mb: 3,
        borderRadius: '12px',
        background: 'linear-gradient(135deg, #0d1b2a 0%, #1b3a5c 60%, #0d2137 100%)',
        ...sx,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          right: -60,
          top: -60,
          width: 280,
          height: 280,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      {icon ? (
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: '12px',
            flexShrink: 0,
            color: '#fff',
            background: 'linear-gradient(135deg, #2065D1, #1B4FA8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 6px 16px rgba(32,101,209,.45)',
            position: 'relative',
            '& svg': {fontSize: 26},
          }}
        >
          {icon}
        </Box>
      ) : null}
      <Box sx={{minWidth: 0, flex: 1, position: 'relative'}}>
        {eyebrow ? <Box sx={{mb: 0.75}}>{eyebrow}</Box> : null}
        <Typography sx={{fontSize: 24, fontWeight: 800, color: '#fff', mb: subtitle ? '6px' : 0, lineHeight: 1.2}}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{fontSize: 13, color: 'rgba(255,255,255,0.6)', maxWidth: 620, lineHeight: 1.7}}>{subtitle}</Typography>
        ) : null}
      </Box>
      {actions ? (
        <Box sx={{position: 'relative', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, flexWrap: 'wrap'}}>
          {actions}
        </Box>
      ) : null}
    </Box>
  )
}

SupplyHero.propTypes = {
  icon: PropTypes.node,
  eyebrow: PropTypes.node,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  actions: PropTypes.node,
  subtle: PropTypes.bool,
  sx: PropTypes.object,
}
